import TimeUnitDropdown from "components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown";
import { DashboardEditorProps } from "components/ComponentTypeTypes";
import { DweenTimeUnitEnum } from "utils/enums";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { InputNumber, InputNumberValueChangeEvent } from "primereact/inputnumber";
import React, { useCallback, useEffect, useState } from "react";
import { T } from "services/lang/LanguageService";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import DatasetMultiSelect from "components/basic/dropdowns/dataset/DatasetMultiSelect";
import { BoundType, ConfidenceIntervalLineChartProps, XAxisType } from "./ConfidenceIntervalLineChartTypes";
import { DatasetLoadingConf } from "../interfaces";

const defaultConfidenceIntervalLineChartConfig: ConfidenceIntervalLineChartProps = {
  identifiers: [],
  boundType: BoundType.CONFIDENCE,
  xAxisType: XAxisType.TIME,
  timeBoundIntervalInSeconds: 60,
  valueBoundsCount: 5,
  loadConf: {
    lag: 5,
    lead: 0,
    alignUnit: DweenTimeUnitEnum.MINUTES,
    lagUnit: DweenTimeUnitEnum.HOURS,
    leadUnit: DweenTimeUnitEnum.HOURS,
  },
  showDateSelection: true,
};

const ConfidenceIntervalLineChartEditor = ({
  config,
  onConfigChange,
}: DashboardEditorProps<ConfidenceIntervalLineChartProps>) => {
  const conf = config && Object.keys(config).length !== 0 ? config : defaultConfidenceIntervalLineChartConfig;

  const [currentConfig, setCurrentConfig] = useState<ConfidenceIntervalLineChartProps>(conf);

  useEffect(() => {
    if (currentConfig) {
      onConfigChange(currentConfig);
    }
  }, [currentConfig]);

  const handleLoadConfDetailsChange = useCallback(
    (e: InputNumberValueChangeEvent | DropdownChangeEvent) => {
      const key = e.target.id as keyof DatasetLoadingConf;
      const value = typeof e.value !== "number" ? e.value.toString().toUpperCase() : e.value;
      setCurrentConfig(prev => ({
        ...prev,
        loadConf: { ...prev.loadConf, [key]: value } as DatasetLoadingConf,
      }));
    },
    [currentConfig.loadConf]
  );

  return (
    <div className="grid p-fluid flex flex-column">
      <div className="flex">
        <div className="flex flex-column col-4">
          <label>{T("label.select-type")}</label>
          <Dropdown
            value={currentConfig.boundType}
            options={Object.values(BoundType).map(value => ({
              label: value,
              value,
            }))}
            onChange={e => setCurrentConfig(prev => ({ ...prev, boundType: e.value as BoundType }))}
          />
        </div>
        <div className="flex flex-column col-4">
          <label>{T("label.select-x-axis-type")}</label>
          <Dropdown
            value={currentConfig.xAxisType}
            options={Object.values(XAxisType).map(value => ({
              label: value,
              value,
            }))}
            onChange={e => setCurrentConfig(prev => ({ ...prev, xAxisType: e.value as XAxisType }))}
          />
        </div>
        {currentConfig.xAxisType === XAxisType.VALUES && (
          <div className="flex flex-column col-4">
            <label>{T("label.select-x-axis-dataset")}</label>
            <DatasetDropdown
              value={currentConfig.identifiers?.[0] ?? null}
              onChange={e => {
                const selectedIdentifier = e ?? "";
                const updatedIdentifiers = [
                  selectedIdentifier,
                  ...(currentConfig.identifiers?.slice(1) ?? []),
                ];
                setCurrentConfig({ ...currentConfig, identifiers: updatedIdentifiers });
              }}
            />
          </div>
        )}
      </div>
      <div className="col-12 flex flex-column mb-3 gap-2">
        <label>{T("label.y-axis-datasets")}</label>
        <DatasetMultiSelect
          value={
            currentConfig.xAxisType === XAxisType.TIME
              ? currentConfig.identifiers
              : currentConfig.identifiers?.slice(1)
          }
          onChange={e => {
            setCurrentConfig({
              ...currentConfig,
              identifiers:
                currentConfig.xAxisType === XAxisType.TIME ? e : [currentConfig.identifiers?.[0], ...e],
            });
          }}
        />
      </div>
      <div className="grid p-fluid flex flex-row">
        <div className="flex flex-column justify-content-between col-2">
          <label htmlFor="updateFrequency">{T("load-conf.update-frequency")}</label>
          <div className="p-inputgroup w-full">
            <InputNumber
              id="updateFrequency"
              value={currentConfig.updateFrequency as number}
              onValueChange={e =>
                setCurrentConfig(prev => ({
                  ...prev,
                  updateFrequency: e.value?.valueOf() ?? undefined,
                }))
              }
            />
            <span className="p-inputgroup-addon">s</span>
          </div>
        </div>
        {currentConfig.xAxisType === XAxisType.TIME ? (
          <div className="flex flex-column justify-content-between col-2">
            <label htmlFor="timeBoundIntervalInSeconds">{T("label.time-bound-interval-seconds")}</label>
            <InputNumber
              id="timeBoundIntervalInSeconds"
              value={currentConfig.timeBoundIntervalInSeconds}
              onValueChange={e =>
                setCurrentConfig(prev => ({
                  ...prev,
                  timeBoundIntervalInSeconds: e.value?.valueOf() ?? undefined,
                }))
              }
            />
          </div>
        ) : (
          <div className="flex flex-column justify-content-between col-2">
            <label htmlFor="valueBoundsCount">{T("label.value-bounds-count")}</label>
            <InputNumber
              id="valueBoundsCount"
              value={currentConfig.valueBoundsCount}
              onValueChange={e =>
                setCurrentConfig(prev => ({
                  ...prev,
                  valueBoundsCount: e.value?.valueOf() ?? undefined,
                }))
              }
            />
          </div>
        )}
        <div className="w-12rem flex flex-column gap-3 col-4">
          <label htmlFor="showDateSelection">{T("chart-config.show-date-selection")}</label>
          <InputSwitch
            id="showDateSelection"
            checked={Boolean(currentConfig.showDateSelection)}
            onChange={e => setCurrentConfig(prev => ({ ...prev, showDateSelection: e.value }))}
          />
        </div>
        <div className="w-10rem flex flex-column gap-3 col-4">
          <label htmlFor="hideRefresh">{T("chart-config.hide-refresh")}</label>
          <InputSwitch
            id="hideRefresh"
            checked={Boolean(currentConfig.hideRefresh)}
            onChange={e => setCurrentConfig(prev => ({ ...prev, hideRefresh: e.value }))}
          />
        </div>
      </div>
      <div className="flex justify-content-between gap-3 ml-2">
        <div className="w-4 gap-2 flex flex-column justify-content-center align-items-center">
          <label htmlFor="lag">{T("load-conf.lag")}</label>
          <div className="p-inputgroup">
            <InputNumber
              id="lag"
              className="w-6"
              value={currentConfig.loadConf?.lag}
              onValueChange={handleLoadConfDetailsChange}
            />
            <TimeUnitDropdown
              id="lagUnit"
              className="p-inputgroup-addon p-0 w-6"
              value={currentConfig.loadConf?.lagUnit}
              onChange={handleLoadConfDetailsChange}
            />
          </div>
        </div>
        <div className="w-4 gap-2 flex flex-column justify-content-center align-items-center">
          <label htmlFor="lead">{T("load-conf.lead")}</label>
          <div className="p-inputgroup">
            <InputNumber
              id="lead"
              className="w-6"
              value={currentConfig.loadConf?.lead}
              onValueChange={handleLoadConfDetailsChange}
            />
            <TimeUnitDropdown
              id="leadUnit"
              className="p-inputgroup-addon p-0 w-6"
              value={currentConfig.loadConf?.leadUnit}
              onChange={handleLoadConfDetailsChange}
            />
          </div>
        </div>
        <div className="w-4 gap-2 flex flex-column justify-content-center align-items-center">
          <label htmlFor="alignUnit">{T("load-conf.align-unit")}</label>
          <TimeUnitDropdown
            id="alignUnit"
            className="w-full"
            value={currentConfig.loadConf?.alignUnit || ""}
            onChange={handleLoadConfDetailsChange}
          />
        </div>
      </div>
    </div>
  );
};

export default ConfidenceIntervalLineChartEditor;
